import * as filepath from "./filepath";

import { FS, fsCopyProps, fsTypeDropbox } from "./fs";
import { FsDir } from "./fsentry";
import {
  dateStringToMs,
  inflect,
  len,
  sleep,
  throwIf,
} from "./util";

import type { DropboxConfig } from "./fs";
import type { HttpProgress } from "./http";
import type { Writable } from "svelte/store";
import { getDropboxClient } from "./fs-dropbox-login";

class FileSystemDropbox extends FS {
  _dbx = null;

  constructor(config: DropboxConfig) {
    super(config);
  }

  get dropboxConfig(): DropboxConfig {
    return this.config as DropboxConfig;
  }

  async getDbx(): Promise<any> {
    if (this._dbx) {
      return this._dbx;
    }
    const c = this.dropboxConfig;
    this._dbx = await getDropboxClient(c.accessToken, c.refreshToken);
    return this._dbx;
  }

  // read fs entries in a given directory
  async readDir(
    dirPath: string,
    force: boolean,
    progress: Writable<string>
  ): Promise<FsDir> {
    return this.loadDir(dirPath, force, progress, async () => {
      const dbx = await this.getDbx();
      let entries = await dbxGetEntriesInDir(dbx, dirPath);
      return FsDir.fromWire(dirPath, dbxEntriesToWire(entries));
    });
  }

  // get publicly visible URL
  async getPublicURL(path: string): Promise<string> {
    console.log(`FSDropbox.getPublicURL: path=${path}`);

    // TODO: maybe cache the links?
    // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesGetTemporaryLink__anchor
    let arg = {
      path: path,
    };
    const dbx = await this.getDbx();
    let rsp = await dbx.filesGetTemporaryLink(arg);
    console.log("rsp:", rsp);
    let result = rsp.result;
    console.log("result:", result);
    return result.link;
  }

  // Generic download function that works using getPublicURL
  // Some filesystems might implement optimized version
  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    console.log(`FSDropbox.downloadFile: path=${path}`);
    // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesDownload__anchor
    // https://dropbox.github.io/dropbox-sdk-js/global.html#FilesDownloadArg
    let arg = {
      path: path,
    };
    const dbx = await this.getDbx();
    let rsp = await dbx.filesDownload(arg);
    // fileBlob is not documented in API docs
    let blob = rsp.result.fileBlob;
    return blob;
  }

  async uploadFile(
    path: string,
    file: Blob,
    httpProgress: HttpProgress
  ): Promise<void> {
    let mode = {
      ".tag": "add",
    };
    let arg = {
      contents: file,
      path: path,
      mode: mode,
      autorename: true,
    };
    // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesUpload
    const dbx = await this.getDbx();
    await dbx.filesUpload(arg);
  }

  // rename a file
  async rename(path: string, newPath: string): Promise<void> {
    console.log(`FSDropbox.rename: path=${path}, newPath=${newPath}`);
    throwIf(path == newPath, "path must be != newPath");
    // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesMoveV2__anchor
    let arg = {
      from_path: path,
      to_path: newPath,
      autorename: true,
      allow_ownership_transfer: false,
    };
    const dbx = await this.getDbx();
    let rsp = await dbx.filesMoveV2(arg);
    console.log("rsp:", rsp);
    let result = rsp.result;
    console.log("result:", result);
  }

  // create a new directory
  async createDirectory(dirPath: string): Promise<void> {
    let arg = {
      path: dirPath,
      autorename: true,
    };
    console.log("before dbx.filesCreateFolderV2 path:", dirPath);
    // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesUpload
    const dbx = await this.getDbx();
    let res = await dbx.filesCreateFolderV2(arg);
    console.log("after dbx.filesCreateFolderV2: res:", res);
  }

  // TODO: use a different method
  async deleteFile(path: string): Promise<void> {
    let paths = [path];
    let entries = [];
    for (let p of paths) {
      p = filepath.stripDirSlash(p);
      const e = {
        path: p,
      };
      entries.push(e);
    }
    let arg = {
      entries: entries,
    };
    const dbx = await this.getDbx();
    let rsp = await dbx.filesDeleteBatch(arg);
    let res = rsp.result; // FilesDeleteBatchLaunch
    console.log("filesDeleteBatch: rsp:", rsp);
    let tag = res[".tag"];
    if (tag == "complete") {
      console.log("complete:", res);
      // TODO: look at the result?
      return;
    }
    if (tag != "async_job_id") {
      // what is "other" tag?
      console.log("unexpected rsp:", res);
      throw new Error(`unsupported tag '${tag}'`);
    }
    const jobId = res["async_job_id"];
    let arg2 = {
      async_job_id: jobId,
    };
    let n = 0;
    while (n < 10) {
      const dbx = await this.getDbx();
      rsp = await dbx.filesDeleteBatchCheck(arg2);
      res = rsp.result;
      console.log("filesDeleteBatchCheck: rsp:", rsp);
      tag = res[".tag"];
      if (tag == "complete") {
        return;
      }
      if (tag == "failed") {
        // TODO: pass error message
        throw new Error("delete failed");
      }
      n++;
      await sleep(1000);
      console.log("re-trying dbx.filesDeleteBatchCheck() n:", n);
    }
    throw new Error("delete failed: taking too long");
  }

  // delete files and directories in this filesystem
  async deleteFiles(
    paths: string[],
    progress: Writable<string>
  ): Promise<void> {
    let entries = [];
    for (let p of paths) {
      p = filepath.stripDirSlash(p);
      const e = {
        path: p,
      };
      entries.push(e);
    }
    let arg = {
      entries: entries,
    };
    let nFiles = len(entries);
    let s = inflect("file", nFiles);
    progress.set(`deleting ${nFiles} ${s}`);
    const dbx = await this.getDbx();
    let rsp = await dbx.filesDeleteBatch(arg);
    let res = rsp.result; // FilesDeleteBatchLaunch
    console.log("filesDeleteBatch: rsp:", rsp);
    let tag = res[".tag"];
    if (tag == "complete") {
      console.log("complete:", res);
      // TODO: look at the result?
      return;
    }
    if (tag != "async_job_id") {
      // what is "other" tag?
      console.log("unexpected result:", res);
      throw new Error(`unsupported tag '${tag}'`);
    }
    const jobId = res["async_job_id"];
    let arg2 = {
      async_job_id: jobId,
    };
    let n = 0;
    while (n < 10) {
      const dbx = await this.getDbx();
      rsp = await dbx.filesDeleteBatchCheck(arg2);
      res = rsp.result;
      console.log("filesDeleteBatchCheck: rsp:", rsp);
      tag = res[".tag"];
      if (tag == "complete") {
        return;
      }
      if (tag == "failed") {
        // TODO: pass error message
        throw new Error("delete failed");
      }
      n++;
      await sleep(1000);
      console.log("re-trying dbx.filesDeleteBatchCheck() n:", n);
    }
    throw new Error("delete failed: taking too long");
  }
}

interface DropboxEntry {
  name: string;
  path_lower: string;
  path_display: string;
  id: string;
  client_modified?: string;
  content_hash?: string;
  is_downloadable?: boolean;
  rev?: string;
  server_modified?: string;
  size?: number;
  sharing_info: any;
  ".tag": string;
}

interface ListFolderResult {
  cursor: string;
  has_more: boolean;
  entries: DropboxEntry[];
}

export const prop_dbx_rev = "rev";
export const prop_dbx_content_hash = "content_hash";
export const prop_dbx_client_modified = "client_modified";

const fileProps = [
  prop_dbx_rev,
  prop_dbx_content_hash,
  prop_dbx_client_modified,
];

async function dbxGetEntriesInDir(
  dbx: any,
  dirName: string
): Promise<DropboxEntry[]> {
  if (dirName == "/") {
    dirName = "";
  }
  // https://dropbox.github.io/dropbox-sdk-js/global.html#FilesListFolderArg
  let arg = { path: dirName };
  // https://dropbox.github.io/dropbox-sdk-js/Dropbox.html#filesListFolder__anchor
  let rsp = await dbx.filesListFolder(arg);
  let result: ListFolderResult = rsp.result;
  let entries = result.entries;
  while (result.has_more) {
    let argCont = {
      cursor: result.cursor,
    };
    rsp = await dbx.filesListFolderContinue(argCont);
    result = rsp.result;
    entries.push(...result.entries);
  }
  return entries;
}

export function makeFileSystemDropbox(c: DropboxConfig): FS {
  // console.log("makeFileSystemDropbox, config:", c);
  let fs = new FileSystemDropbox(c);
  return fs;
}

// set to false for debugging code that handles network failures
let disallowNoAccess = true;

function dbxEntriesToWire(entries: DropboxEntry[]) {
  let res: any[][] = [];
  for (let e of entries) {
    const si = e.sharing_info;
    if (disallowNoAccess && si && si.no_access) {
      continue;
    }
    const tag = e[".tag"];
    // console.log("e:", e);
    let isFile = tag === "file";
    let isFolder = tag === "folder";
    throwIf(!(isFile || isFolder), `unknown entry ${JSON.stringify(e)}`);
    let size = isFile ? e.size : -1;
    let lastMod = isFile ? dateStringToMs(e.server_modified) : 0;
    let children = isFile ? null : [];
    let meta = [null, e.name, size, lastMod, children];
    fsCopyProps(fileProps, e, meta);
    res.push(meta);
  }
  return res;
}
